/**
 * The Insights tab, on real data — and the shared bento primitives that My
 * Learning is built from.
 *
 * Replaces a mock dashboard that invented cohort figures (learner counts,
 * comprehension rates) from a seeded PRNG. Two problems with shipping that to
 * learners: the numbers were fiction, and cohort statistics are not a learner's
 * business anyway. This shows what is true and theirs — their own progress.
 *
 * Aurora Atlas: the panel is a small bento of glass tiles floating on the page's
 * aurora. `BentoTile` / `Eyebrow` / `ProgressRing` live here rather than in
 * learnShared so the two progress surfaces (this tab and /learn/me) stay one
 * design with one implementation.
 */

import { Link } from '@tanstack/react-router';
import { Loader2, Check, PlayCircle, Clock, GraduationCap, ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { glassPanel, inkMuted, subjectMeta } from './learnShared';
import { useLearnerProgress, formatWatchTime, formatRelativeDate } from './useLearnerProgress';

// ----------------------------------------------------------------------------
// Bento primitives
// ----------------------------------------------------------------------------

/** Small-caps wayfinding label. Never large enough to compete with the figure. */
export function Eyebrow({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'text-[10.5px] font-bold uppercase leading-none tracking-[0.16em]',
        inkMuted,
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * One glass tile. `tint` washes the tile in a subject's own hue as LIGHT (a
 * corner gradient), never as a flat fill — the same rule the aurora follows.
 */
export function BentoTile({
  tint,
  className,
  children,
}: {
  tint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('relative overflow-hidden p-5', glassPanel, className)}>
      {tint && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[inherit]"
          style={{ background: `linear-gradient(150deg, ${tint}30, transparent 62%)` }}
        />
      )}
      <div className="relative flex h-full min-w-0 flex-col">{children}</div>
    </div>
  );
}

/**
 * A completion ring. The arc is drawn from a dash array so there is one source
 * of truth for the geometry, and it is decorative-with-a-label: the figure it
 * illustrates is always also written out in text next to it.
 */
export function ProgressRing({
  percent,
  size = 46,
  stroke = 4,
  color = '#5B2EE5',
  label,
}: {
  percent: number;
  size?: number;
  stroke?: number;
  color?: string;
  label?: string;
}) {
  const pct = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="block -rotate-90"
      role="img"
      aria-label={label ?? `${Math.round(pct)}% complete`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={stroke}
        className="stroke-[rgba(25,24,49,0.14)] dark:stroke-[rgba(255,255,255,0.16)]"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${(circumference * pct) / 100} ${circumference}`}
        className="transition-[stroke-dasharray] duration-700 ease-out motion-reduce:transition-none"
      />
    </svg>
  );
}

/**
 * A tile whose whole point is one figure. `accent` colours only the icon — a
 * class rather than an inline colour so it can differ between themes.
 */
export function StatTile({
  eyebrow,
  icon,
  value,
  sub,
  accent,
}: {
  eyebrow: string;
  icon: React.ReactNode;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <BentoTile className="min-h-[132px]">
      <div className="flex items-center gap-2">
        <span className={accent ?? inkMuted} aria-hidden="true">
          {icon}
        </span>
        <Eyebrow>{eyebrow}</Eyebrow>
      </div>
      <div className="flex-1" />
      <p className="font-serif text-[38px] leading-[0.85] tracking-tight tabular-nums">{value}</p>
      {sub && <p className={cn('mt-2 text-[12.5px] leading-[1.45]', inkMuted)}>{sub}</p>}
    </BentoTile>
  );
}

/** The focus ring every interactive surface on these pages shares. */
export const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5B2EE5] focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-[#FBFAF7] dark:focus-visible:ring-offset-[#0D0E16]';

// ----------------------------------------------------------------------------
// The panel
// ----------------------------------------------------------------------------

export function LearnProgressPanel() {
  const { items, loading, failed } = useLearnerProgress();

  if (loading) {
    return (
      <div className="grid place-items-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#5B2EE5]" />
      </div>
    );
  }

  if (failed) {
    return (
      <p className={cn('py-20 text-center text-sm', inkMuted)}>
        Could not load your progress. Reload the page to try again.
      </p>
    );
  }

  const touched = items.filter((i) => i.status !== 'not_started');
  const completed = touched.filter((i) => i.status === 'completed');
  const inProgress = touched.filter((i) => i.status === 'in_progress');
  const watchMs = touched.reduce((sum, i) => sum + i.watchTimeMs, 0);
  const checks = touched.filter((i) => i.linkedTest?.taken);

  if (touched.length === 0) {
    return (
      <div className={cn('mt-8 px-8 py-16 text-center', glassPanel)}>
        <h2 className="font-serif text-[26px] leading-[1.14] tracking-tight">
          Your progress starts here
        </h2>
        <p className={cn('mx-auto mt-3 max-w-sm text-[15px] leading-relaxed', inkMuted)}>
          Watch any lesson and this tab fills in — what you finished, how far you
          got, and how your checks went.
        </p>
      </div>
    );
  }

  const recent = touched
    .slice()
    .sort((a, b) => new Date(b.lastViewedAt ?? 0).getTime() - new Date(a.lastViewedAt ?? 0).getTime())
    .slice(0, 8);

  return (
    <div className="pt-8">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          eyebrow="Completed"
          icon={<Check className="h-4 w-4" />}
          value={String(completed.length)}
          sub={completed.length === 1 ? 'lesson finished' : 'lessons finished'}
          accent="#0E8A80"
        />
        <StatTile
          eyebrow="In progress"
          icon={<PlayCircle className="h-4 w-4" />}
          value={String(inProgress.length)}
          sub={inProgress.length === 1 ? 'lesson open' : 'lessons open'}
          accent="#6D28D9"
        />
        <StatTile
          eyebrow="Time learning"
          icon={<Clock className="h-4 w-4" />}
          value={formatWatchTime(watchMs)}
        />
        <StatTile
          eyebrow="Checks taken"
          icon={<GraduationCap className="h-4 w-4" />}
          value={String(checks.length)}
          sub={checks.length === 0 ? 'none yet' : undefined}
        />
      </div>

      <h3 className="mt-10 font-serif text-[24px] leading-[1.14] tracking-tight">Recent activity</h3>
      <ul className={cn('mt-4 overflow-hidden p-1.5', glassPanel)}>
        {recent.map((item) => {
          const meta = subjectMeta(item.subject);
          return (
            <li key={item.shareToken}>
              <Link
                to="/learn/$shareToken"
                params={{ shareToken: item.shareToken }}
                className={cn(
                  'flex items-center gap-3 rounded-2xl px-3.5 py-3 transition-colors hover:bg-white/60 dark:hover:bg-white/[0.06]',
                  focusRing,
                )}
              >
                <span
                  className="h-8 w-1.5 shrink-0 rounded-full"
                  style={{ background: meta.color }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14.5px] font-medium">{item.title ?? 'Untitled lesson'}</p>
                  <p className={cn('mt-0.5 truncate text-[12.5px]', inkMuted)}>
                    {formatRelativeDate(item.lastViewedAt) || 'not watched yet'}
                  </p>
                </div>
                {item.status === 'completed' ? (
                  <span className="flex shrink-0 items-center gap-1 text-[12.5px] font-semibold text-[#0B6F65] dark:text-[#5FC7B6]">
                    <Check className="h-3.5 w-3.5" /> Completed
                  </span>
                ) : (
                  <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-[#5B2EE5] dark:text-[#B69BF5]">
                    {item.maxPercent}%
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>

      <Link
        to="/learn/me"
        className={cn(
          'mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors',
          glassPanel,
          'rounded-full hover:bg-white/95 dark:hover:bg-white/[0.12]',
          focusRing,
        )}
      >
        See everything in My learning
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
